import {getAnalytics, logEvent, setUserId as setAnalyticsUserId} from 'firebase/analytics'
import {getApp} from 'firebase/app'
import type {User} from 'firebase/auth'
import {getAuth, getIdTokenResult} from 'firebase/auth'
import {getDoc, getDocs, getFirestore, runTransaction} from 'firebase/firestore'
import LogRocket from 'logrocket'
import {useEffect, useState} from 'react'
import {getAdminUserRolesCollection} from '~/firestore/collections.client'
import {getUserAdminDoc, getUserDoc} from '~/firestore/docs.client'
import type {TechDiscUser, TokenClaims, UserAccess, UserAdminInfo, UserSettings,} from '~/types/user'
import {AuthClaim, PremiumFeature} from '~/types/user'

export async function requestUserAccess(user: User): Promise<UserAccess> {
    let claims: AuthClaim[] = []
    let premiumStatus: PremiumFeature = PremiumFeature.NONE
    const analytics = getAnalytics(getApp())

    const decodedToken = await getIdTokenResult(user, true)
    // only set if it matches an enum value
    if (decodedToken) {
        claims = Object.values(AuthClaim).filter(claim => decodedToken?.claims?.[claim])
    }

    const { stripeRole, revenueCatEntitlements } = decodedToken?.claims as {
        stripeRole: PremiumFeature
        revenueCatEntitlements: PremiumFeature[]
    }

    if (stripeRole) {
        if ([PremiumFeature.PRO, PremiumFeature.STORE, PremiumFeature.PLAY].includes(stripeRole)) {
            premiumStatus = stripeRole
        } else {
            const message = 'invalid premium status: ' + stripeRole
            logEvent(analytics, message)
            console.error(message)
        }
    } else if (revenueCatEntitlements) {
        if (revenueCatEntitlements.some(entitlement => entitlement === PremiumFeature.STORE)) {
            premiumStatus = PremiumFeature.STORE
        } else if (revenueCatEntitlements.some(entitlement => entitlement === PremiumFeature.PRO)) {
            premiumStatus = PremiumFeature.PRO
        } else if (revenueCatEntitlements.some(entitlement => entitlement === PremiumFeature.PLAY)) {
            premiumStatus = PremiumFeature.PLAY
        } else {
            {
                const message = 'invalid premium status: ' + revenueCatEntitlements.join(', ')
                logEvent(analytics, message)
                console.error(message)
            }
        }
    }

    return { claims, premiumStatus }
}

export const handleAuth = (
    onUserChange: (user: TechDiscUser | null) => void,
    onUserLoadingChange: (loading: boolean) => void,
) => {
    const analytics = getAnalytics(getApp())
    const auth = getAuth(getApp())

    try {
        return auth.onAuthStateChanged(async newUser => {
            if (!newUser) {
                onUserChange(null)
                onUserLoadingChange(false)
                return
            }

            setAnalyticsUserId(analytics, newUser.uid)

            const ref = getUserDoc(newUser.uid)
            const userDoc = await getDoc(ref)
            if (!userDoc.exists()) {
                return
            }
            const userAccessInfo = await requestUserAccess(newUser)

            const userWithRoles: TechDiscUser = await saveUserRoles(newUser)

            const user = { ...userWithRoles, settings: userDoc.data() as UserSettings }

            LogRocket.identify(user.uid, {
                name: user.displayName || '',
                email: user.email || '',
                subscriptionType: userAccessInfo.premiumStatus,
            })

            onUserChange(user)
            onUserLoadingChange(false)
        })
    } catch (error) {
        onUserLoadingChange(false)
        console.error(error)
    } finally {
        onUserLoadingChange(false)
    }
}

async function saveUserRoles(user: TechDiscUser): Promise<TechDiscUser> {
    const firestore = getFirestore()
    const userAdminRef = getUserAdminDoc(user.uid)

    const decodedToken = await getIdTokenResult(user, true)
    const customClaims = decodedToken?.claims as CustomClaims
    try {
        const userRoles = await getUserRoles({ ...user, customClaims })
        const result = await runTransaction(firestore, async transaction => {
            const userAdminDoc = await transaction.get(userAdminRef)

            let userAdminData: Partial<UserAdminInfo> = userAdminDoc.exists()
                ? userAdminDoc.data()
                : null

            if (!userAdminData || !userAdminData.roles) {
                console.debug('User roles synced successfully.')
                // Update user-admin document with legacy roles
                const defaultRoles = userRoles ?? []
                transaction.set(userAdminRef, { roles: defaultRoles }, { merge: true })
                userAdminData = { roles: defaultRoles }
            }

            return { ...user, ...userAdminData } as TechDiscUser
        })

        return result as TechDiscUser
    } catch (error) {
        console.error('Transaction failed: ', error)
        return user
    }
}

export type CustomClaims = Record<AuthClaim, boolean> & { stripeRole: PremiumFeature }

export async function getUserRoles(user: TechDiscUser & { customClaims: CustomClaims }) {
    if (!user) {
        throw 'Unable to get user admin data: missing-user'
    }

    const { stripeRole, ...customClaims } = user.customClaims
    const userRolesRef = getAdminUserRolesCollection()
    const userRolesData = await getDocs(userRolesRef)
    const allRoles = userRolesData.docs.map(doc => doc.data())
    const tdRoles = allRoles
        ?.filter(role => Object.keys(customClaims).some(claim => role.claimToken === claim))
        .map(role => role.id)
    const premiumRoles =
        stripeRole !== PremiumFeature.NONE
            ? [allRoles.find(role => role.claimToken === stripeRole)?.id]
            : []

    const userRoles = [...tdRoles, ...premiumRoles]
    return userRoles
}

export function useTokenPermissions(): string[] {
    const user = getAuth(getApp()).currentUser
    const [claims, setClaims] = useState<TokenClaims>([])

    useEffect(() => {
        const fetch = async () => {
            if (user) {
                const claims = await fetchTokenAndSetClaims(user)
                setClaims(claims)
            }
        }
        fetch()
    }, [user, user?.refreshToken])

    return claims
}

async function fetchTokenAndSetClaims(user: User) {
    let claims: TokenClaims = []
    const analytics = getAnalytics(getApp())
    const decodedToken = await getIdTokenResult(user, true)
    // only set if it matches an enum value

    if (!decodedToken.claims) {
        return claims
    }

    const preimumFeatures = [PremiumFeature.STORE, PremiumFeature.PRO, PremiumFeature.PLAY]
    const { stripeRole, revenueCatEntitlements = [] } = decodedToken?.claims as ParsedToken
    const revCatClaims =
        revenueCatEntitlements.filter(claim => Object.values(PremiumFeature).includes(claim)) ?? []

    if (stripeRole) {
        if (preimumFeatures.includes(stripeRole)) {
            claims.push(stripeRole)
        } else {
            const message = '(S) invalid premium status: ' + stripeRole
            logEvent(analytics, message)
            console.error(message)
        }
    }

    if (revCatClaims.length) {
        for (const entitlement of revCatClaims) {
            if (preimumFeatures.includes(entitlement)) {
                claims.push(entitlement)
            } else {
                const message = '(RC) invalid premium status: ' + revenueCatEntitlements.join(', ')
                logEvent(analytics, message)
                console.error(message)
            }
        }
    }

    return claims
}

type ParsedToken = {
    stripeRole?: PremiumFeature
    revenueCatEntitlements?: PremiumFeature[]
}
